import type { CSSProperties } from 'react';

import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { isSvgImage } from '@/lib/assets-config';
import { readExplicitImageSize } from '@/lib/blocks/image-size';
import {
  buildFocusPointStyle,
  DEFAULT_FOCUS_POINT,
  pickStringUnion,
  readBoolean,
  readImageValue,
  readNumber,
  readPositiveNumber,
  readString,
} from '@/lib/prop-utils';
import { cn } from '@/lib/utils';
import { getImageAlt, getImageUrl, type ImageValue, isPendingAIImage } from '@/types/image';
import { getDragThumbnailClassName } from '../editor/dnd/drag-visuals';
import { getEditorMediaFrameClassName } from '../editor/media-frame';
import { ImageWithLoading, NoImagePlaceholder } from '../section-image/image-with-loading';
import {
  IMAGE_BLOCK_ASPECT_VALUES,
  IMAGE_BLOCK_FIT_VALUES,
  type ImageBlockAspect,
  type ImageBlockFit,
  resolveImagePresentation,
} from './image-presentation';

export { IMAGE_BLOCK_ASPECT_VALUES, IMAGE_BLOCK_FIT_VALUES };
export type { ImageBlockAspect, ImageBlockFit };
export const IMAGE_BLOCK_SHAPE_VALUES = ['sharp', 'rounded', 'circle'] as const;

export type ImageBlockShape = (typeof IMAGE_BLOCK_SHAPE_VALUES)[number];

interface ImageBlockProps {
  image?: ImageValue | null;
  blockProps?: Record<string, unknown>;
  fullBleed?: boolean;
  shape?: ImageBlockShape;
}

// Maps AI image size identifiers (from the generation service) to aspect-ratio classes.
function imageSizeToAspectClass(imageSize: string | undefined): string {
  switch (imageSize) {
    case 'landscape_16_9':
      return 'aspect-video';
    case 'landscape_4_3':
      return 'aspect-[4/3]';
    case 'portrait_4_3':
      return 'aspect-[3/4]';
    case 'portrait_16_9':
      return 'aspect-[9/16]';
    case 'square_hd':
    case 'square':
      return 'aspect-square';
    default:
      return 'aspect-video';
  }
}

export function ImageBlock({ image, blockProps, fullBleed, shape = 'rounded' }: ImageBlockProps) {
  const isPageEditor = useIsPageEditor();

  const normalizedImage = image ?? readImageValue(blockProps?.image);
  const resolvedFullBleed = readBoolean(blockProps?.fullBleed) ?? fullBleed;
  const resolvedShape = pickStringUnion(
    readString(blockProps?.shape),
    IMAGE_BLOCK_SHAPE_VALUES,
    shape,
  );
  const imageAlt = getImageAlt(normalizedImage);
  const isSvg = isSvgImage(normalizedImage);
  const isCircle = resolvedShape === 'circle';
  const presentation = resolveImagePresentation({
    fit: blockProps?.fit,
    aspect: blockProps?.aspect,
    isSvg,
    isCircle,
  });
  const focusStyle =
    presentation.fit === 'cover'
      ? buildFocusPointStyle(
          readNumber(blockProps?.focusX) ?? DEFAULT_FOCUS_POINT,
          readNumber(blockProps?.focusY) ?? DEFAULT_FOCUS_POINT,
        )
      : undefined;
  const dragThumbnailClassName = getDragThumbnailClassName({ isPageEditor });
  const fitClassName = presentation.fitClassName;
  const hasExplicitImageSize = readExplicitImageSize(blockProps?.imageSize) !== undefined;
  const displayWidth = readPositiveNumber(blockProps?.width);
  const displayHeight = readPositiveNumber(blockProps?.height);
  const hasDisplayAspectRatio =
    presentation.requestedAspect === 'auto' &&
    displayWidth !== undefined &&
    displayHeight !== undefined;
  const stretchesToContainer = resolvedFullBleed || hasExplicitImageSize;
  const displayWidthStyle: CSSProperties | undefined = stretchesToContainer
    ? { width: '100%', maxWidth: '100%' }
    : displayWidth
      ? { width: '100%', maxWidth: `${displayWidth}px` }
      : undefined;
  const displaySizeStyle: CSSProperties | undefined =
    displayWidthStyle && hasDisplayAspectRatio
      ? { ...displayWidthStyle, aspectRatio: `${displayWidth} / ${displayHeight}` }
      : displayWidthStyle;
  let aspectClassName = presentation.aspectClassName;
  if (hasDisplayAspectRatio) aspectClassName = '';
  const isContain = presentation.fit === 'contain';
  const hasImage = !!getImageUrl(normalizedImage) || isPendingAIImage(normalizedImage);
  const editorMediaFrameClassName = getEditorMediaFrameClassName({
    isPageEditor,
    isEmpty: !hasImage,
  });
  const shapeClassName = resolvedShape !== 'sharp' ? 'rounded-md' : undefined;
  const fallback = isPageEditor ? <NoImagePlaceholder /> : undefined;

  if (
    !hasDisplayAspectRatio &&
    presentation.aspect === 'auto' &&
    isPendingAIImage(normalizedImage) &&
    !getImageUrl(normalizedImage)
  ) {
    aspectClassName = imageSizeToAspectClass(normalizedImage.imageSize);
  }

  const imageClassName = cn(
    isContain
      ? cn('max-h-full max-w-full', hasExplicitImageSize && 'w-full')
      : cn('h-full w-full', fitClassName),
    !isContain && presentation.aspect === 'auto' && !hasDisplayAspectRatio && 'min-h-[180px]',
    resolvedShape !== 'sharp' && 'rounded-md',
  );

  if (!isPageEditor && !hasImage) return null;

  if (isCircle) {
    return (
      <div
        className={cn(
          'aspect-square w-full overflow-hidden rounded-full',
          editorMediaFrameClassName,
          dragThumbnailClassName,
        )}
        style={displayWidthStyle}
      >
        <ImageWithLoading
          image={normalizedImage}
          alt={imageAlt}
          className="h-full w-full object-cover"
          style={focusStyle}
          fallback={fallback}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'w-full overflow-hidden',
        isContain && 'flex items-center justify-center',
        dragThumbnailClassName,
        !resolvedFullBleed && shapeClassName,
        editorMediaFrameClassName,
        aspectClassName,
      )}
      style={displaySizeStyle}
    >
      <ImageWithLoading
        image={normalizedImage}
        alt={imageAlt}
        className={imageClassName}
        style={focusStyle}
        fallback={fallback}
      />
    </div>
  );
}
